"use client";

import * as React from "react";
import { XIcon } from "lucide-react";
import { Dialog as SheetPrimitive } from "@base-ui/react/dialog";

import { cx } from "../../cx";
import { Button } from "../button";
import styles from "./sheet.module.css";

export type SheetSide = "left" | "right" | "top" | "bottom";
export type SheetSize = "sm" | "default";

type WithClassName<P> = Omit<P, "className"> & { className?: string };

function Sheet(props: SheetPrimitive.Root.Props) {
  return <SheetPrimitive.Root {...props} />;
}

function SheetTrigger(props: SheetPrimitive.Trigger.Props) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetClose(props: SheetPrimitive.Close.Props) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

function SheetCloseButton() {
  return (
    <SheetPrimitive.Close
      data-slot="sheet-close"
      render={
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Close"
          className={styles.close}
        >
          <XIcon strokeWidth={1.75} />
        </Button>
      }
    />
  );
}

type SheetContentProps = WithClassName<SheetPrimitive.Popup.Props> & {
  side?: SheetSide;
  /** Width rung of a left/right sheet; top/bottom sheets span the viewport. */
  size?: SheetSize;
  showCloseButton?: boolean;
};

function SheetContent({
  className,
  children,
  side = "right",
  size = "default",
  showCloseButton = true,
  ...props
}: SheetContentProps) {
  return (
    <SheetPrimitive.Portal data-slot="sheet-portal">
      <SheetPrimitive.Backdrop
        data-tui=""
        data-slot="sheet-overlay"
        className={styles.backdrop}
      />
      <SheetPrimitive.Popup
        data-tui=""
        data-slot="sheet-content"
        data-side={side}
        data-size={size}
        className={cx(styles.popup, className)}
        {...props}
      >
        {side === "bottom" && (
          <span
            aria-hidden
            data-slot="sheet-grabber"
            className={styles.grabber}
          />
        )}
        {children}
        {showCloseButton && <SheetCloseButton />}
      </SheetPrimitive.Popup>
    </SheetPrimitive.Portal>
  );
}

function SheetHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-header"
      className={cx(styles.header, className)}
      {...props}
    />
  );
}

function SheetBody({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-body"
      className={cx(styles.body, className)}
      {...props}
    />
  );
}

function SheetFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-footer"
      className={cx(styles.footer, className)}
      {...props}
    />
  );
}

function SheetTitle({
  className,
  ...props
}: WithClassName<SheetPrimitive.Title.Props>) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cx(styles.title, className)}
      {...props}
    />
  );
}

export {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
};
